/**
 * @file PDF report blob download/open helpers (bidder, high-volume, no-bids).
 */

import client from './client';
import { appendTimeZoneParam } from '../utils/timezone';

export const downloadBidderThermalMockPDF = async (bidderId, auctionGroupId = null) => {
  if (!auctionGroupId) {
    throw new Error('auctionGroupId is required to download thermal mock PDF');
  }

  const params = new URLSearchParams();
  params.append('auctionGroupId', auctionGroupId);
  appendTimeZoneParam(params);
  const apiUrl = `/reports/bidder/${bidderId}/thermal-mock?${params.toString()}`;
  const response = await client.get(apiUrl, {
    responseType: 'blob',
    timeout: 120000,
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const newWindow = window.open(url, '_blank');

  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Bidder_${bidderId}_thermal_mock.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // REVIEW [MEDIUM]: Revoking the blob URL after 1s can blank the PDF tab if still loading.
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

export const downloadBidderPDF = async (bidderId, auctionGroupId = null) => {
  // Backend requires auctionGroupId
  if (!auctionGroupId) {
    throw new Error('auctionGroupId is required to download bidder PDF');
  }
  
  const params = new URLSearchParams();
  params.append('auctionGroupId', auctionGroupId);
  appendTimeZoneParam(params);
  const apiUrl = `/reports/bidder/${bidderId}?${params.toString()}`;
  const response = await client.get(apiUrl, {
    responseType: 'blob',
    timeout: 120000,
  });

  // Create a blob URL from the PDF data
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);

  // Open PDF in a new tab
  const newWindow = window.open(url, '_blank');

  // If popup blocked, fall back to download
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Fallback to download if popup is blocked
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Bidder_${bidderId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  
  // Clean up the blob URL after a delay to allow the browser to load it
  // The new window/tab maintains its own reference, so we can safely clean up after a short delay
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

export const downloadHighVolumePDF = async (minItems, auctionGroupId = null, { thermal = false } = {}) => {
  // Backend requires auctionGroupId
  if (!auctionGroupId) {
    throw new Error('auctionGroupId is required to download high volume PDF');
  }
  
  const params = new URLSearchParams();
  params.append('minItems', minItems);
  params.append('auctionGroupId', auctionGroupId);
  appendTimeZoneParam(params);
  const reportPath = thermal ? '/reports/high-volume/thermal' : '/reports/high-volume';
  
  try {
    const response = await client.get(`${reportPath}?${params.toString()}`, {
      responseType: 'blob',
      timeout: 120000,
    });
    
    // First check status code - if it's an error status, handle it as an error
    if (response.status < 200 || response.status >= 300) {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(response.data);
      });
      
      let errorMessage = 'Failed to generate PDF report.';
      try {
        const errorData = JSON.parse(text);
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // Check content-type to ensure it's a PDF
    const contentType = response.headers['content-type'] || '';
    if (!contentType.includes('application/pdf')) {
      // Not a PDF - might be an error response
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(response.data);
      });
      
      let errorMessage = 'Failed to generate PDF report.';
      try {
        const errorData = JSON.parse(text);
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // Not JSON, use the text or default message
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }
    
    // Verify the blob is not empty
    if (!response.data || response.data.size === 0) {
      throw new Error('PDF file is empty. No bidders found matching the criteria.');
    }
    
    // Validate PDF by checking first few bytes for PDF signature
    const firstBytes = await response.data.slice(0, 4).arrayBuffer();
    const firstBytesText = new TextDecoder().decode(firstBytes);
    if (!firstBytesText.startsWith('%PDF')) {
      // Not a valid PDF - try to read as error message
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(response.data);
      });
      
      let errorMessage = 'Failed to generate PDF report.';
      
      // Try to parse as JSON error
      try {
        const errorData = JSON.parse(text);
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // Not JSON - check if it's a readable error message
        if (text && text.trim().length > 0 && text.length < 500) {
          // Use the text if it looks like an error message
          errorMessage = text.trim();
        } else {
          // Otherwise use a more specific message
          errorMessage = 'The server returned an invalid PDF file. This may indicate a problem with PDF generation.';
        }
      }
      
      throw new Error(errorMessage);
    }
    
    // Create a blob URL from the PDF data
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Open PDF in a new tab
    const newWindow = window.open(url, '_blank');
    
    // If popup blocked, fall back to download
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      // Fallback to download if popup is blocked
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = thermal
        ? `High_Volume_Bidders_Receipt_${minItems}+_Items_${timestamp}.pdf`
        : `High_Volume_Bidders_${minItems}+_Items_${timestamp}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    
    // Clean up the blob URL after a delay to allow the browser to load it
    // The new window/tab maintains its own reference, so we can safely clean up after a short delay
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
    
  } catch (error) {
    // Handle axios errors (non-2xx status codes)
    if (error.response) {
      const responseData = error.response.data;
      
      // If error response is a blob (when responseType is 'blob'), parse it as JSON
      if (responseData instanceof Blob) {
        try {
          const text = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsText(responseData);
          });
          
          try {
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || 'Failed to generate PDF report.');
          } catch (parseError) {
            // Not JSON - use status code to determine message
            if (error.response.status === 404) {
              throw new Error(`No bidders found with ${minItems} or more items.`);
            }
            throw new Error('Failed to generate PDF report. Please try again.');
          }
        } catch (readError) {
          // Couldn't read blob
          if (error.response.status === 404) {
            throw new Error(`No bidders found with ${minItems} or more items.`);
          }
          throw new Error('Failed to generate PDF report. Please try again.');
        }
      } else if (responseData && typeof responseData === 'object' && responseData.error) {
        // Already parsed JSON error
        throw new Error(responseData.error);
      } else {
        // Generic error based on status
        if (error.response.status === 404) {
          throw new Error(`No bidders found with ${minItems} or more items.`);
        }
        throw new Error(error.message || 'Failed to generate PDF report.');
      }
    }
    
    // Re-throw if it's already a proper Error with a message
    if (error instanceof Error && error.message) {
      throw error;
    }
    
    // Fallback error
    throw new Error('Failed to generate PDF report. Please try again.');
  }
};

export const downloadNoBidsPDF = async (auctionGroupId = null) => {
  const params = new URLSearchParams();
  if (auctionGroupId) {
    params.append('auctionGroupId', auctionGroupId);
  }
  appendTimeZoneParam(params);
  const apiUrl = params.toString() 
    ? `/reports/no-bids?${params.toString()}`
    : `/reports/no-bids`;
  const response = await client.get(apiUrl, {
    responseType: 'blob',
    timeout: 120000,
  });
  
  // Create a blob URL from the PDF data
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  
  // Open PDF in a new tab
  const newWindow = window.open(url, '_blank');
  
  // If popup blocked, fall back to download
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Fallback to download if popup is blocked
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = auctionGroupId 
      ? `No-Bids_AuctionGroup_${auctionGroupId}_${timestamp}.pdf`
      : `No-Bids_${timestamp}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  
  // Clean up the blob URL after a delay to allow the browser to load it
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};

export const downloadNoBidsThermalPDF = async (auctionGroupId = null) => {
  const params = new URLSearchParams();
  if (auctionGroupId) {
    params.append('auctionGroupId', auctionGroupId);
  }
  appendTimeZoneParam(params);
  const apiUrl = params.toString()
    ? `/reports/no-bids/thermal?${params.toString()}`
    : `/reports/no-bids/thermal`;
  const response = await client.get(apiUrl, {
    responseType: 'blob',
    timeout: 120000,
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const newWindow = window.open(url, '_blank');

  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = auctionGroupId
      ? `No-Bids_Receipt_AuctionGroup_${auctionGroupId}_${timestamp}.pdf`
      : `No-Bids_Receipt_${timestamp}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 1000);
};





